import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import {
  BORDER_SOFT,
  BORDER_STRONG,
  Density,
  Icon,
  ListColumn,
  ListFilter,
  ListSortOption,
  SortDir,
  TEAL,
  TEXT,
  TEXT_DIM,
  TEXT_MUTED,
  filterValueLabel,
  fromSearchTime,
  t,
  toSearchTime, sortDirLabelOf } from './listShared';

export interface ListFilterBarProps {
  language: any;
  /** Raw search text; the caller debounces before hitting the API. Omit to hide the box. */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  /** i18n key of the search placeholder. */
  searchPlaceholder?: string;
  searchInputRef?: React.RefObject<HTMLInputElement>;
  filters?: ListFilter[];
  filterValues?: Record<string, string>;
  onFilterChange?: (key: string, value: string) => void;
  /**
   * The Sort control. Rendered only when there is something to sort by:
   * `sortOptions` when given, otherwise the sortable columns.
   */
  columns: ListColumn[];
  sortOptions?: ListSortOption[];
  sortBy?: string;
  sortDir?: SortDir;
  onSortChange?: (sortBy: string, sortDir: SortDir) => void;
  visibleColumns: Record<string, boolean>;
  onToggleColumn: (key: string) => void;
  density: Density;
  onDensityChange: (density: Density) => void;
}

const POPOVER_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  zIndex: 60,
  backgroundColor: '#FFFFFF',
  border: `1px solid ${BORDER_SOFT}`,
  borderRadius: '12px',
  padding: '6px',
  boxShadow: '0 12px 32px rgba(15,118,121,.12)',
  minWidth: '180px',
  maxHeight: '280px',
  overflowY: 'auto',
};

/** The inline calendar is ~260px wide and taller than the option lists. */
const popoverStyle = (isDatePicker: boolean, side: 'left' | 'right'): React.CSSProperties => ({
  ...POPOVER_STYLE,
  ...(side === 'left' ? { left: 0 } : { right: 0 }),
  ...(isDatePicker ? { maxHeight: 'none', overflow: 'visible', minWidth: '272px' } : {}),
});

const OPTION_STYLE: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: '8px',
  fontSize: '13px',
  lineHeight: '18px',
  color: TEXT,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

/** The sort options a filter bar offers: explicit list first, sortable columns second. */
export const sortOptionsOf = (columns: ListColumn[], explicit?: ListSortOption[]): ListSortOption[] =>
  explicit ??
  columns
    .filter(c => c.sortable && c.desktop !== false)
    .map(c => ({ value: c.sortKey ?? c.key, label: c.label }));

/** The value editor of a 'text' filter: one input, applied on Enter or Apply. */
const TextFilterInput: React.FC<{
  language: any;
  initial: string;
  onApply: (value: string) => void;
}> = ({ language, initial, onApply }) => {
  const [value, setValue] = useState<string>(initial);
  return (
    <div className="flex items-center" style={{ gap: '6px', padding: '4px' }}>
      <input
        autoFocus
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') onApply(value.trim());
        }}
        className="font-inter placeholder:text-placeholder bg-transparent outline-none focus-none"
        style={{
          fontSize: '13px',
          flex: 1,
          minWidth: '140px',
          height: '32px',
          padding: '0 10px',
          border: `1px solid ${BORDER_STRONG}`,
          borderRadius: '8px',
          color: TEXT,
        }}
        placeholder={t(language, 'Type to filter', 'Type to filter')}
      />
      <div
        className="cursor-pointer font-inter"
        style={{
          fontSize: '13px',
          fontWeight: 500,
          color: '#FFFFFF',
          backgroundColor: TEAL,
          borderRadius: '8px',
          padding: '7px 12px',
          whiteSpace: 'nowrap',
        }}
        onClick={() => onApply(value.trim())}
      >
        {t(language, 'Apply', 'Apply')}
      </div>
    </div>
  );
};

/**
 * The list toolbar: one search box, removable filter chips, a "+ Filter"
 * control, and the sort / columns / density controls on the right.
 */
const ListFilterBar: React.FC<ListFilterBarProps> = ({
  language,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search',
  searchInputRef,
  filters = [],
  filterValues = {},
  onFilterChange,
  columns,
  sortOptions,
  sortBy = '',
  sortDir = 'DESC',
  onSortChange,
  visibleColumns,
  onToggleColumn,
  density,
  onDensityChange,
}) => {
  const [openMenu, setOpenMenu] = useState<string>('');
  /** Which filter's value list the "+ Filter" popover is currently showing. */
  const [pickingFilter, setPickingFilter] = useState<string | null>(null);
  const barRef = useRef<HTMLDivElement>(null);

  // Close every popover on an outside click — the shell has no overlay layer
  // of its own, so a stray open menu would sit over the table.
  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(event.target as Node)) {
        setOpenMenu('');
        setPickingFilter(null);
      }
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, []);

  const filterOf = (key: string) => filters.find(f => f.key === key) as ListFilter;
  const activeFilters = filters.filter(f => !!filterValues[f.key]);
  const availableFilters = filters.filter(f => !filterValues[f.key]);
  const sorts = sortOptionsOf(columns, sortOptions);
  const hideableColumns = columns.filter(c => c.hideable !== false && c.desktop !== false);
  const sortLabel = (() => {
    const found = sorts.find(o => o.value === sortBy);
    // Nothing sorted yet: say "Sort", not the first option's name.
    return found ? t(language, found.label, found.label) : t(language, 'Sort', 'Sort');
  })();

  const closeAll = () => {
    setOpenMenu('');
    setPickingFilter(null);
  };

  const pickFilterValue = (key: string, value: string) => {
    onFilterChange?.(key, value);
    closeAll();
  };

  /** The value list for one filter, shared by the chip popover and "+ Filter". */
  const renderValuePicker = (filter: ListFilter) => {
    if (filter.kind === 'date') {
      return (
        <div style={{ padding: '6px' }}>
          <DatePicker
            inline
            selected={fromSearchTime(filterValues[filter.key] ?? '')}
            onChange={(d: any) => pickFilterValue(filter.key, d ? toSearchTime(d) : '')}
          />
        </div>
      );
    }
    if (filter.kind === 'text') {
      // Free-text filter: typed value applied on Enter or via the Apply button.
      return (
        <TextFilterInput
          language={language}
          initial={filterValues[filter.key] ?? ''}
          onApply={value => pickFilterValue(filter.key, value)}
        />
      );
    }
    const options = filter.options ?? [];
    if (options.length === 0) {
      return (
        <div style={{ ...OPTION_STYLE, color: TEXT_DIM, cursor: 'default' }}>
          {t(language, 'No options available', 'No options available')}
        </div>
      );
    }
    return (
      <>
        {options.map((option, index) => (
          <div
            key={`${index}-${option.value}`}
            style={OPTION_STYLE}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#F5FAFA')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            onClick={() => pickFilterValue(filter.key, option.value)}
          >
            {option.label}
          </div>
        ))}
      </>
    );
  };

  return (
    <div
      ref={barRef}
      className="flex items-center"
      style={{ gap: '10px', position: 'relative', flexWrap: 'wrap' }}
    >
      {onSearchChange && (
        <div
          className="flex items-center"
          style={{
            width: '210px',
            flex: 'none',
            height: '40px',
            border: `1px solid ${BORDER_STRONG}`,
            borderRadius: '10px',
            gap: '8px',
            padding: '0 12px',
            backgroundColor: '#FFFFFF',
          }}
        >
          <Icon name="search" size={16} />
          <input
            ref={searchInputRef}
            value={searchValue ?? ''}
            onChange={e => onSearchChange(e.target.value)}
            placeholder={t(language, searchPlaceholder, searchPlaceholder)}
            className="font-inter placeholder:text-placeholder bg-transparent outline-none focus-none"
            style={{ fontSize: '14px', flex: 1, minWidth: 0, color: TEXT }}
          />
        </div>
      )}

      {activeFilters.map(filter => (
        <div key={filter.key} style={{ position: 'relative' }}>
          <div
            className="flex items-center cursor-pointer font-inter"
            style={{
              gap: '6px',
              height: '32px',
              padding: '0 10px 0 12px',
              border: `1px solid ${BORDER_STRONG}`,
              borderRadius: '999px',
              backgroundColor: '#FFFFFF',
              fontSize: '13px',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{ color: TEXT_MUTED }}
              onClick={() => {
                setPickingFilter(null);
                setOpenMenu(openMenu === `chip-${filter.key}` ? '' : `chip-${filter.key}`);
              }}
            >
              {t(language, filter.label, filter.label)}:{' '}
            </span>
            <span
              style={{ color: TEXT, fontWeight: 500 }}
              onClick={() => {
                setPickingFilter(null);
                setOpenMenu(openMenu === `chip-${filter.key}` ? '' : `chip-${filter.key}`);
              }}
            >
              {filterValueLabel(filter, filterValues[filter.key], language)}
            </span>
            <span
              onClick={() => {
                closeAll();
                onFilterChange?.(filter.key, '');
              }}
              className="flex items-center"
            >
              <Icon name="close" size={14} />
            </span>
          </div>
          {openMenu === `chip-${filter.key}` && (
            <div style={popoverStyle(filter.kind === 'date', 'left')}>
              {renderValuePicker(filter)}
            </div>
          )}
        </div>
      ))}

      {availableFilters.length > 0 && (
        <div style={{ position: 'relative' }}>
          <div
            className="flex items-center cursor-pointer font-inter"
            style={{
              gap: '6px',
              height: '32px',
              padding: '0 12px',
              borderRadius: '999px',
              border: `1px dashed ${BORDER_STRONG}`,
              color: TEAL,
              fontSize: '13px',
              fontWeight: 500,
            }}
            onClick={() => {
              setPickingFilter(null);
              setOpenMenu(openMenu === 'add-filter' ? '' : 'add-filter');
            }}
          >
            <Icon name="plus" color={TEAL} size={14} />
            <span>{t(language, 'Filter', 'Filter')}</span>
          </div>
          {openMenu === 'add-filter' && (
            <div
              style={popoverStyle(
                pickingFilter !== null && filterOf(pickingFilter)?.kind === 'date',
                'left'
              )}
            >
              {pickingFilter === null
                ? availableFilters.map(filter => (
                    <div
                      key={filter.key}
                      style={OPTION_STYLE}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#F5FAFA')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                      onClick={() => setPickingFilter(filter.key)}
                    >
                      <span style={{ flex: 1 }}>{t(language, filter.label, filter.label)}</span>
                      <Icon name="chev" size={12} color={BORDER_STRONG} />
                    </div>
                  ))
                : renderValuePicker(filterOf(pickingFilter))}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center" style={{ marginLeft: 'auto', gap: '10px' }}>
        {sorts.length > 0 && onSortChange && (
          <div style={{ position: 'relative' }}>
            <div
              className="flex items-center cursor-pointer font-inter"
              style={{
                gap: '8px',
                height: '36px',
                padding: '0 12px',
                border: `1px solid ${BORDER_STRONG}`,
                borderRadius: '10px',
                fontSize: '13px',
                backgroundColor: '#FFFFFF',
                color: TEXT,
              }}
              onClick={() => setOpenMenu(openMenu === 'sort' ? '' : 'sort')}
            >
              <Icon name="sort" size={15} />
              <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{sortLabel}</span>
              <Icon name="chevd" size={14} />
            </div>
            {openMenu === 'sort' && (
              <div style={popoverStyle(false, 'right')}>
                {sorts.map(option => (
                  <div
                    key={option.value}
                    style={{
                      ...OPTION_STYLE,
                      color: option.value === sortBy ? TEAL : TEXT,
                      fontWeight: option.value === sortBy ? 500 : 400,
                    }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#F5FAFA')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    onClick={() => {
                      onSortChange(option.value, sortDir);
                      setOpenMenu('');
                    }}
                  >
                    {t(language, option.label, option.label)}
                  </div>
                ))}
                <div style={{ height: '1px', backgroundColor: BORDER_SOFT, margin: '6px 4px' }} />
                {(['DESC', 'ASC'] as const).map(dir => (
                  <div
                    key={dir}
                    style={{
                      ...OPTION_STYLE,
                      color: dir === sortDir ? TEAL : TEXT,
                      fontWeight: dir === sortDir ? 500 : 400,
                    }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#F5FAFA')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    onClick={() => {
                      onSortChange(sortBy, dir);
                      setOpenMenu('');
                    }}
                  >
                    {sortDirLabelOf(language, dir, sortBy)}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {hideableColumns.length > 0 && (
          <div style={{ position: 'relative' }}>
            <div
              className="flex items-center justify-center cursor-pointer"
              style={{
                width: '36px',
                height: '36px',
                border: `1px solid ${BORDER_STRONG}`,
                borderRadius: '10px',
                backgroundColor: '#FFFFFF',
              }}
              title={t(language, 'Columns', 'Columns')}
              onClick={() => setOpenMenu(openMenu === 'columns' ? '' : 'columns')}
            >
              <Icon name="columns" size={16} />
            </div>
            {openMenu === 'columns' && (
              <div style={popoverStyle(false, 'right')}>
                {hideableColumns.map(column => (
                  <div
                    key={column.key}
                    style={OPTION_STYLE}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#F5FAFA')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    onClick={() => onToggleColumn(column.key)}
                  >
                    <span
                      style={{
                        width: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {visibleColumns[column.key] !== false && (
                        <Icon name="check" size={13} color={TEAL} />
                      )}
                    </span>
                    <span>{t(language, column.label, column.label)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ position: 'relative' }}>
          <div
            className="flex items-center justify-center cursor-pointer"
            style={{
              width: '36px',
              height: '36px',
              border: `1px solid ${BORDER_STRONG}`,
              borderRadius: '10px',
              backgroundColor: '#FFFFFF',
            }}
            title={t(language, 'Density', 'Density')}
            onClick={() => setOpenMenu(openMenu === 'density' ? '' : 'density')}
          >
            <Icon name="sliders" size={16} />
          </div>
          {openMenu === 'density' && (
            <div style={popoverStyle(false, 'right')}>
              {(
                [
                  { value: 'compact', label: 'Compact' },
                  { value: 'default', label: 'Default' },
                  { value: 'comfortable', label: 'Comfortable' },
                ] as { value: Density; label: string }[]
              ).map(option => (
                <div
                  key={option.value}
                  style={{
                    ...OPTION_STYLE,
                    color: option.value === density ? TEAL : TEXT,
                    fontWeight: option.value === density ? 500 : 400,
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#F5FAFA')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                  onClick={() => {
                    onDensityChange(option.value);
                    setOpenMenu('');
                  }}
                >
                  {t(language, option.label, option.label)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListFilterBar;
